/**
 * A shape's fill: one colour, or a gradient. §6.4.
 *
 * The gradient side is deliberately small. A design tool can offer draggable
 * stops on a ramp, mid-point handles and per-stop opacity; a press file cannot
 * carry most of that without either flattening it to an image or producing
 * something a RIP will interpret differently from the screen. What is here maps
 * exactly onto a PDF axial or radial shading, which is why the proof and the
 * press file agree - and anything that could not be said in a shading was left
 * out rather than approximated.
 *
 * Stops sit on the ramp and are dragged along it, and are edited one at a time:
 * choose a stop, then use the same ink editor a solid fill uses. A popover
 * inside a popover would be the alternative, and it is worse on a touch screen.
 */

import { useEffect, useRef, useState } from 'react';
import type { Colour, Fill, Gradient, Ink } from '../model/types';
import { isGradient } from '../model/types';
import { toCss } from '../model/units';
import { InkEditor, describe } from './ColourField';
import { useEditor } from '../state/store';
import { along } from '../colour/picking';

interface Props {
  label: string;
  value: Fill;
  onChange: ( fill: Fill ) => void;
}

/** More than this and the stops are too close together to click. */
const MAX_STOPS = 6;

/** A press cannot hold a smooth ramp below this, and it bands. */
const BAND_RISK = 0.06;

export function FillField( { label, value, onChange }: Props ) {
  const [ open, setOpen ] = useState( false );
  const wrap = useRef<HTMLDivElement>( null );

  useEffect( () => {
    if ( ! open ) return;

    const away = ( event: MouseEvent ) => {
      // The eyedropper's click on the canvas answers this picker; it does not dismiss it.
      if ( useEditor.getState().sampling ) return;

      if ( wrap.current && ! wrap.current.contains( event.target as Node ) ) setOpen( false );
    };

    const key = ( event: KeyboardEvent ) => {
      // Escape while the eyedropper is out puts the dropper down, not this.
      // Otherwise it closes this and is marked handled, caught before the
      // editor's shortcuts, so it does not also deselect the object.
      if ( event.key !== 'Escape' || useEditor.getState().sampling ) return;

      event.preventDefault();
      setOpen( false );
    };

    window.addEventListener( 'mousedown', away );
    window.addEventListener( 'keydown', key, true );

    return () => {
      window.removeEventListener( 'mousedown', away );
      window.removeEventListener( 'keydown', key, true );
    };
  }, [ open ] );

  return (
    <div className="tb-colour" ref={ wrap }>
      <button
        type="button"
        className="tb-colour__button"
        aria-expanded={ open }
        onClick={ () => setOpen( ! open ) }
      >
        <span className="tb-colour__chip" style={ { background: preview( value ) } } />
        <span className="tb-colour__label">{ label }</span>
        <span className="tb-colour__value">
          { isGradient( value ) ? `${ value.stops.length } colours` : describe( value ) }
        </span>
      </button>

      { open && (
        <div className="tb-colour__pop" role="dialog" aria-label={ label }>
          <Editor value={ value } onChange={ onChange } />
        </div>
      ) }
    </div>
  );
}

/** The editor itself: in the popover above, and inline in the colour panel. */
export function FillEditor( props: { value: Fill; onChange: ( fill: Fill ) => void } ) {
  return <Editor { ...props } />;
}

function Editor( { value, onChange }: { value: Fill; onChange: ( fill: Fill ) => void } ) {
  const gradient = isGradient( value ) ? value : null;
  const [ chosen, setChosen ] = useState( 0 );

  const solid: Colour = isGradient( value )
    ? value.stops[ 0 ]?.colour ?? { space: 'cmyk', value: [ 0, 0, 0, 1 ] }
    : value;

  const stop = gradient?.stops[ Math.min( chosen, gradient.stops.length - 1 ) ];

  const change = ( next: Partial<Gradient> ) => {
    if ( gradient ) onChange( { ...gradient, ...next } );
  };

  return (
    <>
      <div className="tb-fill__tabs" role="tablist">
        <button
          type="button"
          role="tab"
          className={ `tb-fill__tab${ gradient ? '' : ' is-on' }` }
          aria-selected={ ! gradient }
          onClick={ () => onChange( solid ) }
        >
          One colour
        </button>
        <button
          type="button"
          role="tab"
          className={ `tb-fill__tab${ gradient ? ' is-on' : '' }` }
          aria-selected={ !! gradient }
          onClick={ () => {
            if ( gradient ) return;

            // Starting from what is already there, so turning it on is a
            // change the customer can see rather than a reset.
            onChange( {
              kind: 'linear',
              angle: 90,
              stops: [
                { at: 0, colour: solid },
                { at: 1, colour: lighten( solid ) },
              ],
            } );
            setChosen( 0 );
          } }
        >
          Gradient
        </button>
      </div>

      { gradient && (
        <>
          <div className="tb-fill__row">
            <label className="tb-field">
              <span className="tb-field__label">Kind</span>
              <select
                className="tb-input"
                value={ gradient.kind }
                onChange={ ( event ) => change( { kind: event.target.value as Gradient['kind'] } ) }
              >
                <option value="linear">Straight</option>
                <option value="radial">From the middle</option>
              </select>
            </label>

            { gradient.kind === 'linear' && (
              <label className="tb-field">
                <span className="tb-field__label">Turn</span>
                <input
                  className="tb-input"
                  type="number"
                  step={ 15 }
                  value={ Math.round( gradient.angle ) }
                  onChange={ ( event ) => change( { angle: parseFloat( event.target.value ) || 0 } ) }
                />
              </label>
            ) }
          </div>

          <Ramp gradient={ gradient } chosen={ chosen } onChoose={ setChosen } onChange={ ( stops ) => change( { stops } ) } />

          <div className="tb-fill__stops">
            { gradient.stops.map( ( each, index ) => (
              <button
                type="button"
                key={ index }
                className={ `tb-fill__stop${ index === chosen ? ' is-on' : '' }` }
                style={ { background: toCss( each.colour ) } }
                title={ `${ Math.round( each.at * 100 ) }%` }
                aria-label={ `Colour ${ index + 1 }` }
                onClick={ () => setChosen( index ) }
              />
            ) ) }

            { gradient.stops.length < MAX_STOPS && (
              <button
                type="button"
                className="tb-fill__add"
                title="Another colour"
                onClick={ () => {
                  const stops = gradient.stops.concat( {
                    at: 1,
                    colour: lighten( gradient.stops[ gradient.stops.length - 1 ]?.colour ?? solid ),
                  } );

                  change( { stops: spread( stops ) } );
                  setChosen( stops.length - 1 );
                } }
              >
                +
              </button>
            ) }
          </div>

          { stop && (
            <>
              <label className="tb-field">
                <span className="tb-field__label">At</span>
                <input
                  className="tb-input"
                  type="number"
                  min={ 0 }
                  max={ 100 }
                  step={ 5 }
                  value={ Math.round( stop.at * 100 ) }
                  onChange={ ( event ) => {
                    const at = Math.min( 1, Math.max( 0, parseFloat( event.target.value ) / 100 ) );
                    const stops = gradient.stops.map( ( each, i ) =>
                      i === chosen ? { ...each, at } : each
                    );

                    change( { stops } );
                  } }
                />
              </label>

              <InkEditor
                value={ stop.colour }
                onChange={ ( colour ) =>
                  change( {
                    stops: gradient.stops.map( ( each, i ) => ( i === chosen ? { ...each, colour } : each ) ),
                  } )
                }
                onClear={
                  gradient.stops.length > 2
                    ? () => {
                        change( { stops: gradient.stops.filter( ( _each, i ) => i !== chosen ) } );
                        setChosen( 0 );
                      }
                    : undefined
                }
              />
            </>
          ) }

          { bands( gradient ) && (
            <p className="tb-fill__warn">
              Two of these colours are very close. A press may show steps rather than
              a smooth blend.
            </p>
          ) }
        </>
      ) }

      { ! isGradient( value ) && <InkEditor value={ value } onChange={ onChange } /> }
    </>
  );
}

/**
 * The ramp, with its stops on it.
 *
 * Drag a stop along to move it; click the ramp between stops to add one there,
 * in the colour the ramp already has at that point, so adding a stop changes
 * nothing until it is changed. Arrow keys move a focused stop by one per cent,
 * and Delete takes it off - the mouse is not the only way in.
 *
 * Always drawn left to right whatever the gradient's own angle: this is a
 * picture of the stops, not of the shape.
 */
function Ramp( {
  gradient,
  chosen,
  onChoose,
  onChange,
}: {
  gradient: Gradient;
  chosen: number;
  onChoose: ( index: number ) => void;
  onChange: ( stops: Gradient['stops'] ) => void;
} ) {
  const ramp = useRef<HTMLDivElement>( null );

  const atPointer = ( clientX: number ) => {
    const box = ramp.current?.getBoundingClientRect();

    if ( ! box || box.width === 0 ) return 0;

    return Math.round( Math.min( 1, Math.max( 0, ( clientX - box.left ) / box.width ) ) * 100 ) / 100;
  };

  const move = ( index: number, at: number ) =>
    onChange( gradient.stops.map( ( each, i ) => ( i === index ? { ...each, at } : each ) ) );

  return (
    <div
      ref={ ramp }
      className="tb-fill__ramp"
      style={ { background: preview( { ...gradient, kind: 'linear', angle: 0 } ) } }
      title={ gradient.stops.length < MAX_STOPS ? 'Click to add a colour here' : undefined }
      onPointerDown={ ( event ) => {
        // A press on the ramp itself, not on a stop, adds one.
        if ( event.target !== event.currentTarget || gradient.stops.length >= MAX_STOPS ) return;

        const at = atPointer( event.clientX );

        onChange( gradient.stops.concat( { at, colour: along( gradient, at ) } ) );
        onChoose( gradient.stops.length );
      } }
    >
      { gradient.stops.map( ( each, index ) => (
        <button
          type="button"
          key={ index }
          className={ `tb-fill__marker${ index === chosen ? ' is-on' : '' }` }
          style={ { left: `${ each.at * 100 }%`, background: toCss( each.colour ) } }
          aria-label={ `Colour ${ index + 1}, at ${ Math.round( each.at * 100 ) }%` }
          onPointerDown={ ( event ) => {
            event.currentTarget.setPointerCapture( event.pointerId );
            onChoose( index );
          } }
          onPointerMove={ ( event ) => {
            if ( event.currentTarget.hasPointerCapture( event.pointerId ) ) move( index, atPointer( event.clientX ) );
          } }
          onKeyDown={ ( event ) => {
            const step = event.shiftKey ? 0.1 : 0.01;

            if ( event.key === 'ArrowLeft' || event.key === 'ArrowRight' ) {
              event.preventDefault();

              const at = each.at + ( event.key === 'ArrowLeft' ? -step : step );

              move( index, Math.round( Math.min( 1, Math.max( 0, at ) ) * 100 ) / 100 );
            }

            if ( event.key === 'Delete' || event.key === 'Backspace' ) {
              // Claimed even when there are only two stops and it does nothing:
              // passed on, Delete would reach the editor and remove the shape.
              event.preventDefault();

              if ( gradient.stops.length > 2 ) {
                onChange( gradient.stops.filter( ( _each, i ) => i !== index ) );
                onChoose( 0 );
              }
            }
          } }
        />
      ) ) }
    </div>
  );
}

/* ---------------------------------------------------------------
 * Helpers
 * ------------------------------------------------------------ */

/** A CSS preview of any fill, for the chip and the ramp. */
function preview( fill: Fill ): string {
  if ( ! isGradient( fill ) ) return toCss( fill );

  const stops = fill.stops
    .slice()
    .sort( ( a, b ) => a.at - b.at )
    .map( ( stop ) => `${ toCss( stop.colour ) } ${ Math.round( stop.at * 100 ) }%` )
    .join( ', ' );

  return fill.kind === 'radial'
    ? `radial-gradient(circle, ${ stops })`
    // CSS angles run clockwise from "up"; ours run from left-to-right, which
    // is 90 degrees round from that.
    : `linear-gradient(${ Math.round( fill.angle ) + 90 }deg, ${ stops })`;
}

/** A second colour that is visibly different from the first. */
function lighten( colour: Colour ): Colour {
  if ( colour.space === 'rgb' ) return { space: 'cmyk', value: [ 0, 0, 0, 0 ] };

  const ink = colour.space === 'spot' ? colour.fallback : colour.value;

  // Thirty per cent of the ink: far enough to see, close enough to look
  // deliberate rather than like two unrelated colours.
  const [ c, m, y, k ] = ink;

  const soften = ( v: number ) => Math.round( v * 30 ) / 100;

  return { space: 'cmyk', value: [ soften( c ), soften( m ), soften( y ), soften( k ) ] as Ink };
}

/** Spreads stops evenly, for when one is added on top of another. */
function spread( stops: Gradient['stops'] ): Gradient['stops'] {
  return stops.map( ( stop, index ) => ( {
    ...stop,
    at: stops.length < 2 ? 0 : index / ( stops.length - 1 ),
  } ) );
}

/**
 * Whether this gradient is likely to band.
 *
 * Two stops close together with colours far apart is the classic cause: the
 * press has to make the whole transition in a few millimetres, and prints it
 * as visible steps. Worth saying before the job goes out, not after.
 */
function bands( gradient: Gradient ): boolean {
  const sorted = gradient.stops.slice().sort( ( a, b ) => a.at - b.at );

  for ( let i = 1; i < sorted.length; i++ ) {
    const gap = ( sorted[ i ]?.at ?? 0 ) - ( sorted[ i - 1 ]?.at ?? 0 );

    if ( gap > 0 && gap < BAND_RISK ) return true;
  }

  return false;
}
